import { useCallback, useState } from "react";
import axios, {
  AxiosResponse,
  Method,
  AxiosError,
  AxiosRequestConfig,
} from "axios";
import { useAuth0 } from "@auth0/auth0-react";

const protocol = import.meta.env.VITE_API_PROTOCOL;
const host = import.meta.env.VITE_API_HOST;
const base = `${protocol}://${host}`;

const redirectErrorCodes = [
  "login_required",
  "consent_required",
  "interaction_required",
  "invalid_grant",
] as const;

type RedirectErrorCode = (typeof redirectErrorCodes)[number];

const isRedirectErrorCode = (code?: string): code is RedirectErrorCode => {
  return Boolean(
    code && redirectErrorCodes.includes(code as RedirectErrorCode),
  );
};

/**
 * Issues authenticated HTTP requests against the API base
 * (built from `VITE_API_PROTOCOL` and `VITE_API_HOST`) and tracks the
 * latest response, error, and loading state.
 *
 * When the user is authenticated, an Auth0 access token is attached as a
 * `Bearer` Authorization header. If silent token retrieval fails with an
 * Auth0 error code that requires re-authentication (`login_required`,
 * `consent_required`, `interaction_required`, `invalid_grant`), the hook
 * triggers `loginWithRedirect`, preserving the current path + query in
 * `appState.returnTo` so the user lands back where they started.
 *
 * @returns An object containing:
 * - `fetch`: function to issue a request (see its own JSDoc for params).
 * - `response`: the most recent successful `AxiosResponse`, or `undefined`.
 * - `error`: the most recent `AxiosError`, or `null` after a successful call.
 * - `loading`: `true` while a request is in flight.
 */
export const useRequest = () => {
  const { getAccessTokenSilently, isAuthenticated, loginWithRedirect } =
    useAuth0();
  const [response, setResponse] = useState<AxiosResponse>();
  const [error, setError] = useState<AxiosError | null>(null);
  const [loading, setLoading] = useState<boolean>(false);

  /**
   * Issue an HTTP request against the configured API base.
   *
   * @param method - HTTP method (e.g. `"GET"`, `"POST"`).
   * @param url - Path appended to the API base URL (include the leading `/`).
   * @param body - Optional request payload sent as `data`.
   * @param timeout - Optional request timeout in milliseconds.
   * @param setContentType - When `true` (default), sets
   *   `Content-Type: application/json`. Pass `false` for requests like
   *   `multipart/form-data` uploads where axios should set the header itself.
   * @returns A promise that resolves to the `AxiosResponse` on success, or
   *   `undefined` on failure (the error is captured in the `error` state).
   */
  const fetch = useCallback(
    (
      method: Method,
      url: string,
      body?: unknown,
      timeout?: number,
      setContentType = true,
    ) => {
      setLoading(true);

      const getAuthHeader = async () => {
        if (!isAuthenticated) {
          return Promise.resolve({});
        }

        return getAccessTokenSilently()
          .then((token) => ({
            Authorization: `Bearer ${token}`,
          }))
          .catch((tokenError) => {
            console.error("Error getting access token:", tokenError);
            const authError = tokenError as { error?: string };

            const shouldRedirect = isRedirectErrorCode(authError?.error);

            if (shouldRedirect) {
              loginWithRedirect({
                appState: {
                  returnTo: window.location.pathname + window.location.search,
                },
              });
            }
            return Promise.reject(tokenError);
          });
      };

      return getAuthHeader()
        .then((authHeader) => {
          const axiosConfig: AxiosRequestConfig = {
            method: method,
            url: `${base}${url}`,
            data: body,
            headers: {
              "Content-Type": "application/json",
              ...authHeader,
            },
            timeout: timeout,
          };
          if (!setContentType) {
            delete axiosConfig?.headers?.["Content-Type"];
          }
          return axios(axiosConfig);
        })
        .then((res) => {
          setResponse(res);
          setError(null);
          return res;
        })
        .catch((err) => {
          setError(err);
        })
        .finally(() => setLoading(false));
    },
    [isAuthenticated, getAccessTokenSilently, loginWithRedirect],
  );

  return {
    fetch,
    response,
    error,
    loading,
  };
};
